// REACT IMPORTS
import React from "react";

// COMPONENT IMPORTS
import Button from "components/form/Button";
import { Link } from "react-router-dom";
import { useCookies } from "react-cookie";
import moment from "moment";
import { useTid } from "utils/testid";
import { activateOnKey } from "utils/activateOnKey";

// PROPS TYPES
interface CookieNoticeProps {
  onAccept: () => void;
  onPreferencesClick?: () => void;
}

/* ------------------------------------- COOKIE NOTICE ------------------------------------- */

const CookieNotice = (props: CookieNoticeProps) => {
  const tid = useTid();
  const { onAccept, onPreferencesClick } = props;
  const [, setCookie] = useCookies([
    "acceptCookies",
    "strictly-necessary",
    "analytics",
    "functional",
    "targeting",
    "acceptCookies",
  ]);

  const acceptClick = (value: string) => {
    setCookie("strictly-necessary", true, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("analytics", value === "acceptCookies", {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("functional", value === "acceptCookies", {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("targeting", value === "acceptCookies", {
      expires: moment().add(3, "months").toDate(),
    });

    setCookie("acceptCookies", value, {
      expires: moment().add(3, "months").toDate(),
    });
    onAccept();
  };

  return (
    <div
      className="w-[var(--shell-vw)] animate__animated animate__fadeInUp"
      data-testid={tid("cookie-notice")}
      role="region"
      aria-label="Cookie notice"
    >
      <div className="bg-btnPrimaryActive lg:p-[30px] p-4 flex lg:flex-row flex-col items-center lg:justify-between justify-center lg:gap-0 gap-[50px]">
        <p className="text-white lg:text-[22px] leading-normal max-w-[1180px]">
          We use necessary cookies to run this portal and, with your
          permission, optional cookies for navigation, analytics, and
          marketing. Choose Preferences to manage them — declining won&apos;t
          affect your payment.&nbsp;
          <Link
            to="https://www.dcdial.com/legal/cookie-policy"
            target="_blank"
            rel="noreferrer"
            className="hover:text-hoverYellow text-yellow2 underline cursor-pointer"
            data-testid={tid("cookie-policy-link")}
            aria-label="Read cookie notice"
          >
            Cookie notice
          </Link>
        </p>
        <div className="md:flex items-center gap-5 hidden">
          <div
            className="px-4 py-[10px] text-xl text-white leading-normal cursor-pointer hover:text-[#d3e7ff]"
            onClick={onPreferencesClick}
            onKeyDown={activateOnKey(onPreferencesClick)}
            role="button"
            tabIndex={0}
            data-testid={tid("cookie-preferences-button")}
            aria-label="Open cookie preferences"
          >
            Preferences
          </div>
          <Button
            variant="white"
            text="Do not sell my information"
            className="w-max font-medium text-blue"
            onClick={() => acceptClick("do_not_sell")}
            data-testid={tid("do-not-sell-button")}
            aria-label="Do not sell my information"
          />
          <Button
            variant="white"
            text="Accept"
            className="w-max font-medium  text-blue"
            onClick={() => acceptClick("acceptCookies")}
            data-testid={tid("accept-cookies-button")}
            aria-label="Accept cookies"
          />
        </div>
        <div className="flex items-center gap-4 md:hidden justify-center flex-wrap">
          <Button
            variant="white"
            text="Do not sell my information"
            className="w-max font-medium text-blue"
            onClick={() => acceptClick("do_not_sell")}
            data-testid={tid("mobile-do-not-sell-button")}
            aria-label="Do not sell my information"
          />
          <Button
            variant="white"
            text="Accept"
            className="w-max font-medium text-blue"
            onClick={() => acceptClick("acceptCookies")}
            data-testid={tid("mobile-accept-cookies-button")}
            aria-label="Accept cookies"
          />
          <div
            className="px-4 lg:py-[10px] py-[2.5px] text-xl text-white leading-normal cursor-pointer hover:text-[#d3e7ff]"
            onClick={onPreferencesClick}
            onKeyDown={activateOnKey(onPreferencesClick)}
            role="button"
            tabIndex={0}
            data-testid={tid("mobile-cookie-preferences-button")}
            aria-label="Open cookie preferences"
          >
            Preferences
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieNotice;
